import React, { useState, useEffect, useRef } from "react";
import {Edit} from "@material-ui/icons";
import LibraryForm from "./LibraryForm/LibraryForm";
import { motion, AnimatePresence } from "framer-motion";
import { appearAndExit } from "../../utils/commonAnimations";
import { formatParagraphs } from "../../utils/utils";
import classes from './LibraryComponent.module.css';
import mergingStyles from '../Merging/Merging.module.css'
import IconWithHover from "../IconButtons/IconWithHover/IconWithHover";
import InfoButton from "../IconButtons/InfoButton/InfoButton";
import useClickOutside from "../../customHooks/useClickOutside/useClickOutside";
import ReactHtmlParser from "react-html-parser";

const LibraryComponent = ({ uniqueId, parentId, name, startDesc, popoverText, getIcon, popoverHoverCloseOnClick = false, onClick, itemKey, isMerging, children, update, infoContent, useSelect, startingSelect = [], options = [], getOptions = () => [], fromStyles = false, noDesc = false, liveEdit = false}) => {
    const [editIsOpen, setEditIsOpen] = useState(false);
    const [description, setDescription] = useState(startDesc);
    const [selectOptions, setSelectOptions] = useState(options);
    const formRef = useRef();
    const isMounted = useRef(true); // Track mounted state

    const ref = useRef(null);

    useEffect(() => {
        // Clean up on unmount
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (getOptions) {
            const options = getOptions();
            if (isMounted.current) {
                setSelectOptions(options);
            }
        }
    }, [editIsOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isMounted.current) {
            setDescription(startDesc);
        }
    }, [startDesc])

    function submitEdit(allowOpen = false) {
        if (liveEdit && editIsOpen && formRef && formRef.current) {
            const currentDesc = formRef.current.getCurrentDesc();
            const currSelectInfo = formRef.current.getCurrentSelect();
            if (isMounted.current) {
                setDescription(currentDesc)
                setEditIsOpen(false)
                // update should be called last
                update({id: uniqueId, description: currentDesc, select: currSelectInfo});
            }
        } else if (allowOpen) {
            if (isMounted.current) {
                setEditIsOpen((prev) => !prev)
            }
        }
    }

    // closes when you click outside if using live edit.
    useClickOutside(ref, () => {
        if (isMounted.current && liveEdit && editIsOpen) {
            submitEdit()
        }
    })

    const icon = getIcon && getIcon()

    const iconWithHover = icon && <IconWithHover icon={icon} popoverContent={popoverText}/>

    const editButton = (
        <Edit
            style={{ float: "right" }}
            className={"edit-icon"}
            onClick={() => submitEdit(true)}
        />
    );

    const infoButton = infoContent && <InfoButton infoContent={infoContent}/>

    return (
        // ${fromStyles ? mergingStyles.FromBorderColorThicker : ''} you can use this if you want same border color as keywords, it also makes it thicker. use FromBorderColor just for color
        <motion.div
            ref={ref}
            className={`${classes.Border} `}
            variants={appearAndExit}
            initial={false}  // Disable initial animation only on the first render
            animate="open"
            exit="closed"
            key={itemKey}
        >
            {/*${mergingStyles.FromTextColor} for text color. Text color would probably need to change if you wanted this.*/}
            <div className={`${noDesc ? classes.TitleOnly : classes.Title} ${fromStyles ? `${mergingStyles.FromColor} ` : ''}`}>
                <div className={classes.LeftBox}>
                    {iconWithHover}
                    <span className={`${onClick ? classes.Clickable : ''} `} onClick={onClick}>{name}</span>
                </div>
                <div className={'right-box'}>
                    {isMerging && editButton}
                    {infoContent && !isMerging && infoButton}
                </div>
            </div>
            {!noDesc && <div className={classes.TextBody}>
                <AnimatePresence exitBeforeEnter initial={false}>
                    {editIsOpen ? (
                        <motion.div
                            key="editForm"
                            variants={appearAndExit}
                            initial="closed"  // Animates on open/close without affecting initial mount
                            animate="open"
                            exit="closed"
                        >
                            <LibraryForm
                                ref={formRef}
                                isMerging={isMerging}
                                isOpen={true}
                                useSelect={useSelect}
                                startingSelect={startingSelect}
                                options={selectOptions}
                                onEditComplete={(description, select) => {
                                    if (isMounted.current) {
                                        setDescription(description);
                                        setEditIsOpen(false);
                                        update({id: uniqueId, description: description, select: select});
                                    }
                                }}
                                onEditCancel={() => {
                                    if (isMounted.current) {
                                        setEditIsOpen(false);
                                    }
                                }}
                                startingDesc={description}
                                liveEdit={liveEdit}
                            />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="description"
                            variants={appearAndExit}
                            initial="closed"  // Animates on open/close without affecting initial mount
                            animate="open"
                            exit="closed"
                        >

                            {ReactHtmlParser(description)}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>}
            <div className={classes.Children}>{children}</div>
        </motion.div>
    );
};

export default LibraryComponent;
